import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@app/router/routes';
import { useLanguage } from '@shared/contexts/LanguageContext';
import { getPhysicalGiftExchangeRuleList } from '../api/profileService';
import PhysicalGiftExchangeModal from '../components/PhysicalGiftExchangeModal';
import { CARD_STYLES } from '@features/wish-tree/constants';

// ─── 日期格式化工具 ───────────────────────────────────────────────────────────
function fmtDate(gmtStr) {
  if (!gmtStr) return '';
  const d = new Date(gmtStr);
  if (isNaN(d)) return gmtStr;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function fmtDateShort(gmtStr) {
  if (!gmtStr) return '';
  const d = new Date(gmtStr);
  if (isNaN(d)) return gmtStr;
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
}

function fmtTimeShort(gmtStr) {
  if (!gmtStr) return '';
  const d = new Date(gmtStr);
  if (isNaN(d)) return '';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

// ─── 可收合區塊 ───────────────────────────────────────────────────────────────
function CollapsibleSection({ title, subtitle, totalPoints, children, onRuleClick }) {
  const { t, currentLanguage } = useLanguage();
  const isEn = currentLanguage?.code === 'en';
  const [open, setOpen] = useState(true);
  return (
    <div className="flex flex-col w-full">
      <div
        className="flex items-center gap-4"
        style={{ alignSelf: 'stretch', paddingLeft: 26, borderLeft: '6px solid #BC313D' }}
      >
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <span
              style={{ color: '#171717', fontSize: isEn ? 16 : (title.length > 3 ? 20 : 24), fontFamily: 'Noto Serif TC', fontWeight: 700, lineHeight: '1.4', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
            >{title}</span>
            {subtitle && <span style={{ color: '#BC313D', fontSize: isEn ? 16 : (title.length > 3 ? 20 : 24), fontFamily: 'Noto Serif TC', fontWeight: 700, lineHeight: '1.4', display: 'block' }}>{subtitle}</span>}
          </div>
          {totalPoints != null && <span style={{ color: '#BC313D', fontSize: 20, fontFamily: 'Noto Sans TC', fontWeight: 500, lineHeight: '28px', letterSpacing: 0.38, verticalAlign: 'bottom', marginLeft: 16 }}>
            {totalPoints > 0 ? '+' + totalPoints : totalPoints}
          </span>}
        </div>
        <button onClick={onRuleClick} style={{ border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', flexShrink: 0 }} aria-label={t('profile.viewRulesAria')}>
          <span className="material-symbols-outlined text-[#6E625B]/40 text-base flex-shrink-0" style={{ fontVariationSettings: "'FILL' 0", fontSize: 20, verticalAlign: 'top' }}>info</span>
        </button>
        <button
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 flex-shrink-0"
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
        >
          <svg
            width="24" height="24" viewBox="0 0 24 24" fill="none"
            style={{ transition: 'transform 0.2s', transform: open ? 'rotate(0deg)' : 'rotate(180deg)' }}
          >
            <path d="M7 15L12 10L17 15" stroke="rgba(110,98,91,0.30)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
      {open && (
        <div style={{ paddingLeft: 32 }}>
          {children}
        </div>
      )}
    </div>
  );
}

function TimelineDivider() {
  return <div style={{ height: 16 }} />;
}

// ─── 可 scroll 列表容器 ───────────────────────────────────────────────────────
const PREVIEW_COUNT = 3;
const EXPANDED_MAX_HEIGHT = 480;

function ScrollableList({ loading, empty, items = [], renderItem }) {
  const { t } = useLanguage();
  const [expanded, setExpanded] = useState(false);
  const hasMore = items.length > PREVIEW_COUNT;
  const displayed = expanded ? items : items.slice(0, PREVIEW_COUNT);

  return (
    <div style={{ paddingTop: 24, paddingBottom: 24 }}>
      {loading ? (
        <span style={{ color: 'rgba(110,98,91,0.40)', fontSize: 14, fontFamily: 'Noto Sans TC' }}>{t('profile.loading')}</span>
      ) : items.length === 0 ? (
        <span style={{ color: 'rgba(110,98,91,0.40)', fontSize: 14, fontFamily: 'Noto Sans TC' }}>{empty}</span>
      ) : (
        <>
          <div
            className="flex flex-col gap-2"
            style={expanded ? { maxHeight: EXPANDED_MAX_HEIGHT, overflowY: 'auto' } : {}}
          >
            {displayed.map((item, i) => renderItem(item, i))}
          </div>
          {hasMore && (
            <button
              onClick={() => setExpanded((v) => !v)}
              className="flex items-center justify-center gap-1 w-full"
              style={{ marginTop: 12, background: 'none', border: 'none', cursor: 'pointer', padding: '6px 0' }}
            >
              <span style={{ color: '#6E625B', fontSize: 14, fontFamily: 'Noto Sans TC', fontWeight: 400 }}>
                {expanded ? t('profile.collapse') : t('profile.expandAll').replace('{{count}}', items.length)}
              </span>
              <svg
                width="16" height="16" viewBox="0 0 16 16" fill="none"
                style={{ transition: 'transform 0.2s', transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
              >
                <path d="M4 6L8 10L12 6" stroke="#6E625B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          )}
        </>
      )}
    </div>
  );
}

// ─── 寄送明信片卡片 ───────────────────────────────────────────────────────────
function PostcardCard({ item, onClick }) {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const imageUrl = '/image/postcard/pc_4.png';

  return (
    <div
      className="w-full py-3 flex justify-start items-center gap-2 flex-wrap content-center cursor-pointer hover:brightness-95 active:scale-95 transition-all duration-150"
      onClick={() => {
        onClick ? onClick() : navigate(ROUTES.POSTCARD_MY);
      }}
    >
      <div className="flex-1 px-2 py-1 bg-White-White_60/60 rounded-sm outline outline-1 outline-offset-[-1px] outline-[#171717]/15 flex justify-center items-center gap-2">
        <div className="mx-2 my-1 flex-1 rounded-[5px] flex justify-between items-center gap-2">
          <div className="inline-flex flex-col justify-center items-center overflow-hidden flex-shrink-0 gap-1">
            <img className="w-14 h-14 sm:h-12 object-contain" src={imageUrl} alt="postcard" />
          </div>
          <div className="flex-1 px-1 sm:w-32 justify-center text-stone-500 text-lg font-['Noto_Sans_TC'] leading-6 sm:leading-7">
            {t('profile.postcardSentCount').replace('{{count}}', item?.total ?? 0)}
          </div>
          <div className="text-center justify-center text-[#bc313d] sm:text-xl text-lg font-bold font-['Noto_Sans_TC'] leading-5 sm:leading-6 flex-shrink-0">
            +{item?.total_point ?? 0}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── 願心樹卡片 ───────────────────────────────────────────────────────────────
function WishTreeCard({ item, onClick }) {
  const { t } = useLanguage();
  // const imageUrl = '/image/wishtree/tree.png';
  const imageUrl = CARD_STYLES[0].image_s;

  return (
    <div
      className="w-full py-3 flex justify-start items-center gap-2 flex-wrap content-center cursor-pointer hover:brightness-95 active:scale-95 transition-all duration-150"
      onClick={onClick}
    >
      <div className="flex-1 px-2 py-1 bg-White-White_60/60 rounded-sm outline outline-1 outline-offset-[-1px] outline-[#171717]/15 flex justify-center items-center gap-2">
        <div className="mx-2 my-1 flex-1 rounded-[5px] flex justify-between items-center gap-2">
          <div className="inline-flex flex-col justify-center items-center overflow-hidden flex-shrink-0 gap-1">
            <img className="w-14 h-14 sm:h-12 object-contain" src={imageUrl} alt="wish tree" />
          </div>
          <div className="flex-1 px-1 sm:w-32 justify-center text-stone-500 text-lg font-['Noto_Sans_TC'] leading-6 sm:leading-7">
            {t('profile.wishTreeVowCount').replace('{{count}}', item?.total ?? 0)}
          </div>
          <div className="text-center justify-center text-[#bc313d] sm:text-xl text-lg font-bold font-['Noto_Sans_TC'] leading-5 sm:leading-6 flex-shrink-0">
            +{item?.total_point ?? 0}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── 護法DNA 紀錄卡片 ─────────────────────────────────────────────────────────
const DNA_IMAGE_MAP = {
  1: '/image/dna/dna_sun.png',
  2: '/image/dna/dna_rock.png',
  3: '/image/dna/dna_river.png',
  4: '/image/dna/dna_ground.png',
  5: '/image/dna/dna_cloud.png',
  6: '/image/dna/dna_sky.png'
};

function DnaCard({ item }) {
  const navigate = useNavigate();
  const imageUrl = DNA_IMAGE_MAP[item.archetype_id] || '/image/dna/dna_sun.png';
  const dateStr = item.date ? fmtDate(item.date) : '';

  return (
    <div
      className="w-full py-3 sm:py-6 flex justify-start items-center gap-2 flex-wrap content-center cursor-pointer hover:brightness-95 active:scale-95 transition-all duration-150"
      onClick={() => {
        if (item.record_id) {
          navigate(ROUTES.DNA_ANSWER, { state: { recordId: item.record_id, fromPage: 'profile' } });
        }
      }}
    >
      <div className="flex-1 px-2 py-1 bg-White-White_60/60 rounded-sm outline outline-1 outline-offset-[-1px] outline-[#171717]/15 flex justify-center items-center gap-2">
        <div className="mx-2 my-1 flex-1 rounded-[5px] flex justify-between items-center gap-2">
          <div className="inline-flex flex-col justify-center items-center overflow-hidden flex-shrink-0">
            <img className="w-14 h-14 sm:h-12 object-contain" src={imageUrl} alt={item.name} />
            <div className="text-center justify-center text-[#bc313d] sm:text-xl text-lg font-bold font-['Noto_Sans_TC'] leading-5 sm:leading-6">+{item.point ?? 0}</div>
          </div>
          <div className="flex-1 px-1 sm:w-32 justify-center text-stone-500 text-lg font-bold font-['Noto_Sans_TC'] leading-6 sm:leading-7">
            {item.name}
          </div>
          <div className="text-center justify-center text-stone-500/50 text-md sm:text-base font-bold font-['Noto_Sans_TC'] leading-5 sm:leading-6 flex-shrink-0">
            {dateStr}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── 中台知多少 紀錄卡片 ──────────────────────────────────────────────────────
function ChungTaiQuizCard({ item }) {
  const { t } = useLanguage();
  const [showTooltip, setShowTooltip] = useState(false);
  const dateObj = new Date(item.chapter_start_at);
  const m = String(dateObj.getMonth() + 1).padStart(2, '0');
  const d = String(dateObj.getDate()).padStart(2, '0');
  const dateStr = `${m}/${d}`;

  const handleClick = () => {
    setShowTooltip(true);
    setTimeout(() => setShowTooltip(false), 2500);
  };

  return (
    <div className="w-full pr-2.5 bg-White-White_30/30 rounded-sm outline outline-1 outline-offset-[-1px] outline-[#171717]/15 flex justify-start items-center gap-3 relative">
      {showTooltip && (
        <div className="absolute left-0 -top-9 z-50 bg-stone-700 text-white text-xs rounded px-2 py-1 max-w-[90%] leading-snug pointer-events-none">
          {item.chapter_content}
        </div>
      )}
      <div
        data-color="gold"
        data-state="on"
        className="px-2 pt-1 pb-[5px] rounded-sm outline outline-1 outline-offset-[-1px] outline-[#c59b5a] flex justify-center items-center gap-1.5 bg-[#c59b5a] min-w-0 max-w-[55%] cursor-pointer"
        onClick={handleClick}
      >
        <span className="justify-start text-amber-50 font-medium font-['Noto_Sans_TC'] leading-7 flex-shrink-0">
          {dateStr}
        </span>
        <span className="text-amber-50 text-sm font-normal font-['Noto_Sans_TC'] leading-7 truncate">
          {item.chapter_content}
        </span>
      </div>
      <div className="flex-1 min-w-0 flex justify-start items-center gap-1 overflow-hidden">
        <span className="sr-only">{item.chapter_content}</span>
        <span className="text-center justify-center text-stone-500 text-lg font-bold font-['Noto_Sans_TC'] leading-7 flex-shrink-0">
          {item.correct_quantity ? item.correct_quantity : "-"}
        </span>
        <span className="text-center justify-center text-Black-Black_30/30 text-base font-normal font-['Noto_Sans_TC'] leading-6 flex-shrink-0">
          /
        </span>
        <span className="text-center justify-center text-Black-Black_30/30 text-base font-normal font-['Noto_Sans_TC'] leading-6 flex-shrink-0">
          {item.chapter_quantity ? item.chapter_quantity : 0}
        </span>
      </div>
      <div className="flex justify-start items-center gap-1 flex-shrink-0">
        <span className="text-center justify-center font-['Noto_Sans_TC'] leading-7"
          style={(item.point == null && item.round_id == null ? { fontSize: 12, color: '#6E625B' } : { color: '#bc313d', fontSize: 18, fontWeight: 700 })}>
          {item.point == null && item.round_id == null ? t('profile.noRecord') : `+${item.point ?? 0}`}
        </span>
      </div>
    </div>
  );
}

// ─── 吉祥物 格狀紀錄 ─────────────────────────────────────────────────────────
function MascotGrid({ items, loading }) {
  const navigate = useNavigate();
  const { t, currentLanguage } = useLanguage();
  const isEn = currentLanguage?.code === 'en';

  const MASCOT_Y_AXIS = [
    t('profile.mascotYAxisWalk'),
    t('profile.mascotYAxisStay'),
    t('profile.mascotYAxisSit'),
    t('profile.mascotYAxisRest'),
  ];
  const MASCOT_X_AXIS = [
    t('profile.mascotXAxisGoldChild'),
    t('profile.mascotXAxisElephant'),
    t('profile.mascotXAxisLion'),
    t('profile.mascotXAxisDeer'),
  ];

  if (loading) {
    return <div className="pl-8 py-6 text-[rgba(110,98,91,0.40)] text-sm">{t('profile.loading')}</div>;
  }
  if (!items || items.length === 0) {
    return <div className="pl-8 py-6 text-[rgba(110,98,91,0.40)] text-sm">{t('profile.noRecord')}</div>;
  }

  const map = {};
  items.forEach((item) => {
    map[`${item.category_y}-${item.category_x}`] = item;
  });

  return (
    <div className="w-full" style={isEn ? { marginRight: -12, width: 'calc(100% + 12px)' } : {}}>
      <div className="w-full py-6 flex flex-col justify-start items-start gap-2 md:gap-5">
        <div className="flex w-full justify-between items-end gap-0 md:gap-2 lg:gap-4">
          <div className="w-10 md:w-12 lg:w-16 flex justify-center items-center shrink-0 mr-2">
            <div className="w-10 text-center text-stone-500 text-xs md:text-sm lg:text-base font-normal font-['Noto_Sans_TC'] leading-5 break-keep">{t('profile.mascotYAxisPosture')}</div>
          </div>
          {MASCOT_X_AXIS.map((x) => (
            <div key={x} className={`flex-1 flex justify-center text-center text-stone-500 font-normal font-['Noto_Sans_TC'] leading-4 px-0.5 md:max-w-none ${isEn ? 'max-w-[56px]' : ''}`} style={{ fontSize: 10, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', wordBreak: 'break-word' }}>
              {x}
            </div>
          ))}
        </div>
        {MASCOT_Y_AXIS.map((y) => (
          <div key={y} className="flex w-full justify-between items-start gap-0 md:gap-2 lg:gap-4">
            <div className="w-10 md:w-12 lg:w-16 self-stretch flex justify-center items-start pt-4 flex-shrink-0 mr-2">
              <div className="w-10 text-center text-stone-500 text-xs md:text-sm lg:text-base font-normal font-['Noto_Sans_TC'] leading-5 break-keep">{y}</div>
            </div>
            {MASCOT_X_AXIS.map((x) => {
              const item = map[`${y}-${x}`];
              const isCollected = item?.is_collected;
              return (
                <div key={x} className={`flex-1 flex flex-col justify-center items-center gap-0 md:gap-1 md:max-w-none ${isEn ? 'max-w-[56px]' : ''}`}>
                  <div className="w-full aspect-square max-w-[44px] md:max-w-[64px] lg:max-w-[80px] relative rounded-sm outline outline-1 outline-offset-[-1px] outline-black/10 flex justify-center items-center overflow-hidden"
                    style={!isCollected ? { backgroundColor: 'var(--color-neutral-100)' } : undefined}
                  >
                    {item?.reward_media ? (
                      <img
                        className="object-contain w-full h-full p-1"
                        src={item.reward_media}
                        alt={`${x}_${y}`}
                        style={!isCollected ? { filter: 'grayscale(90%)' } : undefined}
                      />
                    ) : (
                      <div className="w-full h-full" />
                    )}
                  </div>
                  <div className="flex flex-col justify-start items-center">
                    <div className="text-center text-[#bc313d] text-sm md:text-base lg:text-lg font-medium font-['Noto_Sans_TC'] leading-6 min-h-[24px]">
                      {item?.point === 0 ? "-0" : item?.point ?? " "}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <button
          onClick={() => navigate(ROUTES.REDEEM)}
          className="flex items-center gap-1.5"
        >
          <span style={{ color: '#BC313D', fontSize: 16, fontFamily: 'Noto Serif TC', fontWeight: 500, textDecoration: 'underline', textUnderlineOffset: 4 }}>{t('profile.goToRedeemMascot')}</span>
          <span className="material-symbols-outlined text-lg" style={{ color: '#BC313D' }}>arrow_forward</span>
        </button>
      </div>
    </div>
  );
}

// ─── 祈福點燈 格狀紀錄 ───────────────────────────────────────────────────────
function VowGrid({ items, loading, onItemClick }) {
  const { t, currentLanguage } = useLanguage();
  const isEn = currentLanguage?.code === 'en';

  if (loading) {
    return <div className="pl-8 py-6 text-[rgba(110,98,91,0.40)] text-sm">{t('profile.loading')}</div>;
  }
  if (!items || items.length === 0) {
    return <div className="pl-8 py-6 text-[rgba(110,98,91,0.40)] text-sm">{t('profile.noRecord')}</div>;
  }

  const VOW_CATEGORIES = [
    { name: t('profile.blessingHealth'), color: 'yellow' },
    { name: t('profile.blessingFamily'), color: 'orange' },
    { name: t('profile.blessingDharma'), color: 'red' },
    { name: t('profile.blessingStudy'), color: 'green' },
    { name: t('profile.blessingCareer'), color: 'blue' },
    { name: t('profile.blessingConnection'), color: 'purple' },
  ];
  const VOW_LAMP_IMG = [
    "/image/lamp/yellow_x2.png",
    "/image/lamp/orange_x2.png",
    "/image/lamp/red_x2.png",
    "/image/lamp/green_x2.png",
    "/image/lamp/blue_x2.png",
    "/image/lamp/purple_x2.png",
  ];

  const buildRowItems = (categoryData) => {
    const innerItems = categoryData?.items || [];
    const maxSeqNo = innerItems.length > 0 ? Math.max(...innerItems.map(it => it.seq_no)) : 0;
    const row = [];
    for (let i = 0; i < maxSeqNo; i++) {
      row.push(innerItems.find(it => it.seq_no === (i + 1)) || null);
    }
    return row;
  };

  return (
    <div className="w-full">
      <div className="w-full py-6 flex flex-col justify-center items-start gap-2">
        <div className="flex w-full py-2 items-center flex-wrap gap-1 md:flex-nowrap md:gap-4 lg:gap-6">
          <div className="px-2 py-1 rounded-lg flex flex-col justify-center items-center gap-1 w-16 md:w-20 lg:w-24 flex-shrink-0">
            <div className="text-center text-stone-500 text-xs md:text-sm lg:text-base font-normal font-['Noto_Sans_TC'] leading-5 break-keep">類別</div>
          </div>
          <div className="flex-1 grid grid-cols-4 gap-1 md:gap-[18px]">
            {[1, 2, 3, 4].map(num => (
              <div key={num} className="flex justify-center items-center">
                <div className="text-center text-stone-500 text-xs md:text-sm lg:text-base font-normal font-['Noto_Sans_TC'] leading-5">{num}</div>
              </div>
            ))}
          </div>
        </div>

        {items.map((categoryData, rowIdx) => {
          const rowItems = buildRowItems(categoryData);
          const catColor = VOW_CATEGORIES[rowIdx]?.color;
          const collectAllPoint = categoryData.collect_all_point;
          return (
            <div key={categoryData.category_title} className="flex w-full border-b border-[#6E625B]/10 items-center flex-wrap gap-1 md:flex-nowrap py-2 md:gap-4 lg:gap-6">
              <div data-state="on" data-type="theme" className="py-1 rounded-lg flex flex-col justify-start items-center gap-1 w-16 md:w-20 lg:w-24 flex-shrink-0">
                <div data-color={catColor} className="w-10 h-10 md:w-12 md:h-12 relative overflow-hidden">
                  <img className="w-10 h-10 md:w-12 md:h-12 left-0 top-0 absolute object-contain" src={VOW_LAMP_IMG[rowIdx]} alt={categoryData.category_title} />
                </div>
                <div className={`flex flex-col justify-start ${isEn ? 'items-center' : 'items-start'}`}>
                  <div className={`text-black text-[13px] sm:text-[15px] md:text-base font-normal font-['Noto_Sans_TC'] leading-6 break-keep ${isEn ? 'text-center' : 'justify-center'}`}>{categoryData.category_title}</div>
                </div>
              </div>
              <div className="flex-1 flex flex-col justify-center items-center w-full min-w-0">
                <div className="grid grid-cols-4 w-full gap-1 md:gap-[18px]">
                  {rowItems.map((item, idx) => (
                    <div key={idx} className="flex justify-center items-center">
                      {item && (
                        <button
                          data-state="on"
                          data-type="sign2"
                          className="w-9 h-16 md:w-12 md:h-20 bg-White-White_60/60 rounded-sm outline outline-1 outline-offset-[-1px] outline-[#6E625B]/20 flex flex-col justify-center items-center flex-shrink-0 cursor-pointer hover:brightness-95 active:scale-95 transition-all duration-150"
                          style={item.is_collected === true ? {} : { outline: '1px solid transparent' }}
                          onClick={() => onItemClick && onItemClick({ contentId: item.content_id, categoryName: categoryData.category_title })}
                        >
                          <div className="w-6 h-12 md:w-8 md:h-16 relative">
                            <img
                              className="w-7 h-16 md:w-9 md:h-20 left-[-1.50px] top-[-9px] md:left-[-2px] md:top-[-10px] absolute max-w-none"
                              src={"/image/else/sign.png"}
                              alt="vow"
                              style={!item.is_collected ? { filter: 'grayscale(100%) opacity(0.3)' } : {}}
                            />
                            <div className="w-[40px] md:w-[4px] top-[5px] -left-[8px] sm:-left-[5px] md:left-0 md:top-[14px] absolute text-center justify-center text-[#bc313d] text-xs sm:text-sm md:text-lg font-bold font-['Noto_Sans_TC'] leading-9">
                              {item.point === 0 ? "" : (`+${item.point}` ?? "")}
                            </div>
                          </div>
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {collectAllPoint != null && (
                  <div className="w-[96%] sm:w-full max-w-[320px] px-2 py-1.5 bg-[#C59B5A]/20 rounded-lg flex justify-center items-center gap-1 mt-2 md:mt-2 ml-0 md:ml-4">
                    <div className="text-center text-[#6E625B] text-[11px] sm:text-xs font-medium font-['Noto_Sans_TC'] leading-normal sm:leading-4 whitespace-normal break-words">
                      {t('profile.vowRowAllCollected').replace('{{point}}', collectAllPoint)}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── 券形進度條（整體是一個圓角矩形，左右邊緣中點各凹進一個約 1/3 圓弧，仿票券撕線） ───
// 用單一連續路徑（矩形四邊 + 兩段凹弧）即時依量測寬高運算，讓描邊只沿著真實輪廓走，
// 凹陷處只留下露出的那段弧線，不會像「矩形 - 完整圓」那樣把被遮住的另一半圓也描出來。
function CouponShapePath({ width, height, radius = 12, notchRadius }) {
  const r = Math.min(radius, height / 2);
  const nr = notchRadius ?? height / 5;
  const cy = height / 2;
  return `M${r} 0
    H${width - r}
    A${r} ${r} 0 0 1 ${width} ${r}
    L${width} ${cy - nr}
    A${nr} ${nr} 0 0 0 ${width} ${cy + nr}
    L${width} ${height - r}
    A${r} ${r} 0 0 1 ${width - r} ${height}
    H${r}
    A${r} ${r} 0 0 1 0 ${height - r}
    L0 ${cy + nr}
    A${nr} ${nr} 0 0 0 0 ${cy - nr}
    L0 ${r}
    A${r} ${r} 0 0 1 ${r} 0
    Z`;
}

function CouponProgressBar({ lines, stageLabel, qualified, redeemed, redeemedAt, onExchange }) {
  const { t } = useLanguage();
  const containerRef = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 64 });
  const rightWidth = 104;
  const clickable = qualified && !redeemed;
  const dimmed = !qualified || redeemed;

  useEffect(() => {
    const measure = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setSize({ width: rect.width, height: rect.height });
      }
    };
    measure();
    window.addEventListener('resize', measure);
    // 文字換行會改變卡片實際高度（高度不再寫死），用 ResizeObserver 讓 SVG 外框跟著重新量測。
    const ro = new ResizeObserver(measure);
    if (containerRef.current) ro.observe(containerRef.current);
    return () => {
      window.removeEventListener('resize', measure);
      ro.disconnect();
    };
  }, []);

  const clipId = useRef(`coupon-clip-${Math.random().toString(36).slice(2)}`).current;
  const shadowId = useRef(`coupon-shadow-${Math.random().toString(36).slice(2)}`).current;
  const shapePath = size.width > 0 ? CouponShapePath({ width: size.width, height: size.height }) : null;
  const strokeColor = !qualified && !redeemed ? 'rgba(23, 23, 23, 0.20)' : '#C59B5A';
  const rightBgColor = redeemed ? 'rgba(197, 155, 90, 0.16)' : !qualified ? 'rgba(23, 23, 23, 0.5)' : '#C59B5A';

  return (
    <button
      ref={containerRef}
      type="button"
      disabled={!clickable}
      onClick={onExchange}
      className="relative w-full"
      style={{
        minHeight: 64,
        border: 'none',
        background: 'none',
        padding: 0,
        textAlign: 'left',
        cursor: clickable ? 'pointer' : 'not-allowed',
      }}
    >
      {shapePath && (
        <svg width={size.width} height={size.height} viewBox={`0 0 ${size.width} ${size.height}`} style={{ position: 'absolute', inset: 0, opacity: dimmed ? 0.5 : 1, transition: 'opacity 0.2s', overflow: 'visible' }}>
          <defs>
            <clipPath id={clipId}>
              <path d={shapePath} />
            </clipPath>
            <filter id={shadowId} x="-20%" y="-20%" width="140%" height="140%">
              <feComponentTransfer in="SourceAlpha" result="invertedAlpha">
                <feFuncA type="table" tableValues="1 0" />
              </feComponentTransfer>
              <feGaussianBlur in="invertedAlpha" stdDeviation="4" result="blurAlpha" />
              <feFlood floodColor="#C59B5A" floodOpacity="0.7" result="shadowColor" />
              <feComposite in="shadowColor" in2="blurAlpha" operator="in" result="shadowClipped" />
              <feComposite in="shadowClipped" in2="SourceAlpha" operator="in" result="innerShadow" />
              <feMerge>
                <feMergeNode in="SourceGraphic" />
                <feMergeNode in="innerShadow" />
              </feMerge>
            </filter>
          </defs>
          <g clipPath={`url(#${clipId})`}>
            <rect x={size.width - rightWidth} y="0" width={rightWidth} height={size.height} fill={rightBgColor} />
            {redeemed && (
              <rect x={size.width - rightWidth} y="0" width={rightWidth} height={size.height} fill={rightBgColor} filter={`url(#${shadowId})`} />
            )}
          </g>
          <path d={shapePath} fill="none" stroke={strokeColor} strokeWidth="1.5" />
        </svg>
      )}
      <div className="relative flex items-stretch" style={{ minHeight: 64 }}>
        <div className="flex-1 flex flex-col justify-center gap-1" style={{ minWidth: 0, paddingTop: 8, paddingBottom: 8, paddingLeft: 24, paddingRight: 8 }}>
          {lines.map((line, i) => (
            <div key={i} className="flex items-center justify-between gap-2">
              <span style={{ color: 'rgba(23,23,23,0.60)', fontSize: 13, fontFamily: 'Noto Sans TC', fontWeight: 350, letterSpacing: 0.1, whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.4 }}>{line.text}</span>
              <SuccessIcon done={line.checked} />
            </div>
          ))}
        </div>
        <div className="flex items-center justify-center flex-shrink-0" style={{ width: rightWidth, paddingTop: 8, paddingBottom: 8 }}>
          {redeemed ? (
            <div className="flex flex-col items-center">
              <span style={{ color: '#C59B5A', fontSize: 14, fontFamily: 'Noto Sans TC', fontWeight: 500, letterSpacing: 0.48, whiteSpace: 'normal', textAlign: 'center', lineHeight: 1.3 }}>
                {t('profile.physicalGiftRedeemed')}
              </span>
              {redeemedAt && (
                <span style={{ color: '#C59B5A', fontSize: 10, fontFamily: 'Noto Sans TC', fontWeight: 400, letterSpacing: 0.2, whiteSpace: 'normal', textAlign: 'center', lineHeight: 1.3, opacity: 0.8 }}>
                  {fmtDateShort(redeemedAt)}
                </span>
              )}
              {redeemedAt && (
                <span style={{ color: '#C59B5A', fontSize: 10, fontFamily: 'Noto Sans TC', fontWeight: 400, letterSpacing: 0.2, whiteSpace: 'normal', textAlign: 'center', lineHeight: 1.3, opacity: 0.8 }}>
                  {fmtTimeShort(redeemedAt)}
                </span>
              )}
            </div>
          ) : (
            <span style={{ color: 'white', fontSize: 16, fontFamily: 'Noto Sans TC', fontWeight: 500, letterSpacing: 0.48, whiteSpace: 'normal', textAlign: 'center', lineHeight: 1.3 }}>
              {stageLabel}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

function SuccessIcon({ done }) {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
      <path d="M7 0.875C8.62445 0.875 10.1824 1.52031 11.331 2.66897C12.4797 3.81763 13.125 5.37555 13.125 7C13.125 8.62445 12.4797 10.1824 11.331 11.331C10.1824 12.4797 8.62445 13.125 7 13.125C5.37555 13.125 3.81763 12.4797 2.66897 11.331C1.52031 10.1824 0.875 8.62445 0.875 7C0.875 5.37555 1.52031 3.81763 2.66897 2.66897C3.81763 1.52031 5.37555 0.875 7 0.875ZM6.23711 8.20859L4.87676 6.84687C4.77831 6.74825 4.64472 6.69277 4.50537 6.69264C4.36601 6.69251 4.23232 6.74775 4.13369 6.84619C4.03506 6.94464 3.97958 7.07823 3.97946 7.21758C3.97933 7.35694 4.03456 7.49063 4.13301 7.58926L5.8666 9.32422C5.91526 9.37312 5.9731 9.41193 6.0368 9.43841C6.1005 9.46489 6.16881 9.47852 6.23779 9.47852C6.30678 9.47852 6.37508 9.46489 6.43878 9.43841C6.50249 9.41193 6.56033 9.37312 6.60898 9.32422L10.1965 5.73535C10.2464 5.68687 10.2862 5.62894 10.3135 5.56495C10.3409 5.50095 10.3552 5.43216 10.3557 5.36257C10.3562 5.29298 10.3429 5.22399 10.3165 5.1596C10.2901 5.09521 10.2512 5.03671 10.202 4.9875C10.1527 4.9383 10.0942 4.89936 10.0299 4.87297C9.96546 4.84657 9.89647 4.83324 9.82688 4.83375C9.75729 4.83426 9.6885 4.84859 9.62451 4.87593C9.56051 4.90326 9.50258 4.94305 9.4541 4.99297L6.23711 8.20859Z"
        fill={done ? '#00A600' : '#171717'} fillOpacity={done ? 1 : 0.2} />
    </svg>
  );
}

// 文案（標題、門檻分數、任務條件）前端寫死，API 只提供 is_qualified / is_redeemed 狀態；
// rule_id 對應後端的兌換規則，用來匹配該顯示哪一組固定文案。
const SPECIAL_REWARD_STAGE_CONFIG = {
  1: { stageLabel: '5000', titleKey: 'specialRewardStage1Title', pointsKey: 'specialRewardStage1Points', taskKey: 'specialRewardStage1Task' },
  2: { stageLabel: '10000', titleKey: 'specialRewardStage2Title', pointsKey: 'specialRewardStage2Points', taskKey: 'specialRewardStage2Task' },
  3: { stageLabel: '15000', titleKey: 'specialRewardStage3Title', pointsKey: 'specialRewardStage3Points', taskKey: 'specialRewardStage3Task' },
  4: { stageLabel: '20000', titleKey: 'specialRewardStage4Title', pointsKey: 'specialRewardStage4Points', taskKey: 'specialRewardStage4Task' },
};

// ─── 特別獎勵 ─────────────────────────────────────────────────────────────────
function SpecialRewardSection({ onRuleClick }) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(true);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeRuleId, setActiveRuleId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getPhysicalGiftExchangeRuleList()
      .then((res) => {
        if (!cancelled) setItems(res?.items ?? []);
      })
      .catch((err) => console.error('Failed to fetch physical gift exchange rules:', err))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const handleExchanged = (rule_id) => {
    setItems((prev) => prev.map((it) => (it.rule_id === rule_id ? { ...it, is_redeemed: true } : it)));
  };

  const activeConfig = SPECIAL_REWARD_STAGE_CONFIG[activeRuleId];

  if (!loading && items.length === 0) return null;

  return (
    <div className="flex flex-col" style={{ width: '100%' }}>
      <div
        className="flex items-center gap-4"
        style={{ alignSelf: 'stretch', paddingLeft: 26, borderLeft: '6px solid #BC313D' }}
      >
        <span style={{ flex: 1, minWidth: 0, color: 'rgba(23,23,23,0.94)', fontSize: 24, fontFamily: 'Noto Serif TC', fontWeight: 700, lineHeight: '28px' }}>
          {t('profile.sectionSpecialReward')}
        </span>
        <button onClick={onRuleClick} style={{ border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', flexShrink: 0 }} aria-label={t('profile.viewRulesAria')}>
          <span className="material-symbols-outlined text-[#6E625B]/40 text-base flex-shrink-0" style={{ fontVariationSettings: "'FILL' 0", fontSize: 20, verticalAlign: 'top' }}>info</span>
        </button>
        <button
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 flex-shrink-0"
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
        >
          <svg
            width="24" height="24" viewBox="0 0 24 24" fill="none"
            style={{ transition: 'transform 0.2s', transform: open ? 'rotate(0deg)' : 'rotate(180deg)' }}
          >
            <path d="M7 15L12 10L17 15" stroke="rgba(110,98,91,0.30)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
      {open && (
        <div className="flex flex-col" style={{ paddingTop: 16, paddingBottom: 16, paddingLeft: 32, gap: 8 }}>
          {loading ? (
            <span style={{ color: 'rgba(110,98,91,0.40)', fontSize: 14, fontFamily: 'Noto Sans TC' }}>{t('profile.loading')}</span>
          ) : (
            items.map((item) => {
              const config = SPECIAL_REWARD_STAGE_CONFIG[item.rule_id];
              if (!config) return null;
              const checkboxEntries = (item.rule_text || []).filter((entry) => entry?.is_checkbox);
              const pointChecked = checkboxEntries[0]?.checked;
              const taskChecked = checkboxEntries[1]?.checked;
              return (
                <CouponProgressBar
                  key={item.rule_id}
                  lines={[
                    { text: t(`profile.${config.pointsKey}`), checked: !!pointChecked },
                    { text: t(`profile.${config.taskKey}`), checked: !!taskChecked },
                  ]}
                  stageLabel={config.stageLabel}
                  qualified={item.is_qualified}
                  redeemed={item.is_redeemed}
                  redeemedAt={item.created_at}
                  onExchange={() => setActiveRuleId(item.rule_id)}
                />
              );
            })
          )}
        </div>
      )}
      <PhysicalGiftExchangeModal
        isOpen={activeRuleId != null}
        onClose={() => setActiveRuleId(null)}
        ruleId={activeRuleId}
        ruleTitle={activeConfig ? t(`profile.${activeConfig.titleKey}`) : ''}
        onExchanged={handleExchanged}
      />
    </div>
  );
}

// ─── 百寶袋 Tab ───────────────────────────────────────────────────────────────
export default function TreasureBagTab({ quizItems, quizLoading, quizTotalPoints, mascotItems, mascotLoading, mascotTotalPoints, vowItems, vowLoading, vowTotalPoints, dnaItems, dnaLoading, dnaTotalPoints, postcardItem, postcardLoading, wishTreeItem, wishTreeLoading, onRuleClick, onVowItemClick, onPostcardItemClick, onWishTreeItemClick }) {
  const { t } = useLanguage();
  return (
    <div className="flex flex-col w-full gap-4" style={{ position: 'relative' }}>
      <div style={{ position: 'absolute', left: 6, top: 0, bottom: 0, width: 1, background: '#6E625B', pointerEvents: 'none' }} />

      {/* 特別獎勵 */}
      <SpecialRewardSection onRuleClick={() => onRuleClick('gift')} />
      <TimelineDivider />

      {/* 吉祥物 */}
      <CollapsibleSection title={t('profile.sectionMascot')} totalPoints={mascotTotalPoints} onRuleClick={() => onRuleClick('mascot')} >
        <MascotGrid items={mascotItems} loading={mascotLoading} />
      </CollapsibleSection>
      <TimelineDivider />

      {/* 祈福點燈 獲得法語 */}
      <CollapsibleSection title={t('profile.sectionBlessingLamp')} subtitle={t('profile.sectionDharmaTeaching')} totalPoints={vowTotalPoints} onRuleClick={() => onRuleClick('points')}>
        <VowGrid items={vowItems} loading={vowLoading} onItemClick={onVowItemClick} />
      </CollapsibleSection>
      <TimelineDivider />

      {/* 護法DNA */}
      <CollapsibleSection title={t('profile.sectionDharmaDNA')} totalPoints={dnaTotalPoints} onRuleClick={() => onRuleClick('points')}>
        <ScrollableList
          loading={dnaLoading}
          empty={t('profile.noQuizRecords')}
          items={dnaItems}
          renderItem={(item, index) => <DnaCard key={item.record_id || index} item={item} />}
        />
      </CollapsibleSection>
      <TimelineDivider />

      {/* 明信片 */}
      <CollapsibleSection title={t('profile.sectionPostcard')} totalPoints={postcardItem?.total_point} onRuleClick={() => onRuleClick('points')}>
        <div style={{ paddingTop: 24, paddingBottom: 24 }}>
          {postcardLoading ? (
            <span style={{ color: 'rgba(110,98,91,0.40)', fontSize: 14, fontFamily: 'Noto Sans TC' }}>{t('profile.loading')}</span>
          ) : postcardItem?.total > 0 || postcardItem?.total_point > 0 ? (
            <PostcardCard item={postcardItem} onClick={onPostcardItemClick} />
          ) : (
            <span style={{ color: 'rgba(110,98,91,0.40)', fontSize: 14, fontFamily: 'Noto Sans TC' }}>{t('profile.noRecord')}</span>
          )}
        </div>
      </CollapsibleSection>
      <TimelineDivider />

      {/* 願心樹 */}
      <CollapsibleSection title={t('profile.sectionWishTree')} totalPoints={wishTreeItem?.total_point} onRuleClick={() => onRuleClick('points')}>
        <div style={{ paddingTop: 24, paddingBottom: 24 }}>
          {wishTreeLoading ? (
            <span style={{ color: 'rgba(110,98,91,0.40)', fontSize: 14, fontFamily: 'Noto Sans TC' }}>{t('profile.loading')}</span>
          ) : wishTreeItem?.total > 0 || wishTreeItem?.total_point > 0 ? (
            <WishTreeCard item={wishTreeItem} onClick={onWishTreeItemClick} />
          ) : (
            <span style={{ color: 'rgba(110,98,91,0.40)', fontSize: 14, fontFamily: 'Noto Sans TC' }}>{t('profile.noRecord')}</span>
          )}
        </div>
      </CollapsibleSection>
      <TimelineDivider />

      {/* 中台知多少 */}
      <CollapsibleSection title={t('profile.sectionChungTai')} totalPoints={quizTotalPoints} onRuleClick={() => onRuleClick('points')}>
        <ScrollableList
          loading={quizLoading}
          empty={t('profile.noQuizRecords')}
          items={(quizItems || [])}
          renderItem={(item, index) => <ChungTaiQuizCard key={item.round_id ?? index} item={item} />}
        />
      </CollapsibleSection>
      <TimelineDivider />
    </div>
  );
}
